import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, ArrowForward, Close } from './ui/icons';
import { useTheme } from '../context/ThemeContext';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { APP_CONFIG } from '../config/version';

/**
 * Slide-up "new version available" banner (TRINIS brand). Shows when the running
 * build is older than the latest deployed version (reported by checkAppVersion).
 * "Update" clears caches and reloads; dismiss is remembered per-version. Adapted
 * from the AESA transport-platform-new banner with the TRINIS palette (#ff6b35).
 */
const BRAND = '#ff6b35';
const BRAND_LIGHT = '#ff8c5a';

const VersionUpdateBanner: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { versionInfo, isChecking, functionsAvailable } = useVersionCheck();

  const isDismissedFromStorage = useMemo(() => {
    if (!versionInfo?.latestVersion) return false;
    return localStorage.getItem('versionUpdateDismissed') === versionInfo.latestVersion;
  }, [versionInfo?.latestVersion]);

  const [isDismissedManually, setIsDismissedManually] = useState(false);
  const isDismissed = isDismissedManually || isDismissedFromStorage;

  if (
    !APP_CONFIG.versionCheck.showBanner ||
    !versionInfo?.isUpdateAvailable ||
    isDismissed ||
    !functionsAvailable ||
    versionInfo.currentVersion === versionInfo.latestVersion
  ) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissedManually(true);
    if (versionInfo.latestVersion) {
      localStorage.setItem('versionUpdateDismissed', versionInfo.latestVersion);
    }
  };

  const handleUpdate = () => {
    localStorage.removeItem('versionUpdateDismissed');
    const reload = () => window.location.reload();
    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch((err) => {
          console.error('Failed to clear caches:', err);
          return null;
        })
        .finally(reload);
    } else {
      reload();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: 'min(calc(100% - 32px), 560px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderRadius: 16,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          backgroundColor: isDarkMode ? 'rgba(28, 25, 23, 0.88)' : 'rgba(255, 255, 255, 0.92)',
          border: `1.5px solid ${isDarkMode ? 'rgba(255, 107, 53, 0.28)' : 'rgba(255, 107, 53, 0.22)'}`,
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.45)'
            : '0 8px 32px rgba(255, 107, 53, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
          }}
        >
          <Rocket size={20} color="#fff" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: isDarkMode ? '#fafaf9' : '#1c1917',
              lineHeight: 1.3,
              fontSize: '0.875rem',
            }}
          >
            {t('version.newVersionAvailable')}
          </div>
          <div
            style={{
              color: isDarkMode ? 'rgba(250, 250, 249, 0.5)' : 'rgba(28, 25, 23, 0.5)',
              lineHeight: 1.3,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 2,
              fontSize: '0.75rem',
            }}
          >
            v{versionInfo.currentVersion}
            <ArrowForward size={12} />
            <span style={{ color: BRAND, fontWeight: 600 }}>v{versionInfo.latestVersion}</span>
          </div>
        </div>

        <button
          onClick={handleUpdate}
          disabled={isChecking}
          style={{
            borderRadius: 12,
            fontWeight: 600,
            fontSize: '0.8125rem',
            padding: '10px 24px',
            minHeight: 44,
            background: isChecking
              ? (isDarkMode ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.2)')
              : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            color: '#fff',
            border: 'none',
            cursor: isChecking ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        >
          {isChecking ? (t('version.checking') ?? '…') : t('version.update')}
        </button>

        <button
          type="button"
          aria-label={t('common.close')}
          onClick={handleDismiss}
          style={{
            color: isDarkMode ? 'rgba(250, 250, 249, 0.4)' : 'rgba(28, 25, 23, 0.3)',
            flexShrink: 0,
            width: 44,
            height: 44,
            background: 'none',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Close size={18} />
        </button>
      </div>
    </div>
  );
};

export default VersionUpdateBanner;
